import React from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import Image from 'next/image'

import {
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'

import { Modal as DarkModal } from '@hivewatch/dark'

import { Modal } from '@/components/overlays'
import { FileThumbnail } from '@/components/ui'

import { IncidentNoteVideoPlayer } from './IncidentNoteVideoPlayer'
import {
  darkModalBodyStyles,
  modalBodyStyles,
  previewImageStyles,
} from './PreviewMedia.styles'

export const PreviewMedia = ({
  onClose,
  isOpen,
  previewFile,
}: {
  onClose: () => void
  isOpen: boolean
  previewFile: File
}) => {
  const isRebrand = useFeatureFlag(FeatureFlag.incidentDrawerRebrand)
  const colorScheme = useLightMode()

  const renderContent = () => {
    if (previewFile?.type.startsWith('image/')) {
      return (
        <Image
          alt={previewFile.name}
          height={800}
          src={URL.createObjectURL(previewFile)}
          style={previewImageStyles}
          width={800}
        />
      )
    }

    if (previewFile?.type.startsWith('application/pdf')) {
      return (
        <embed
          data-testid='AddNote:embed'
          height={800}
          src={URL.createObjectURL(previewFile)}
          type='application/pdf'
          width='100%'
        />
      )
    }

    if (previewFile?.type.startsWith('video/')) {
      return (
        <IncidentNoteVideoPlayer videoUrl={URL.createObjectURL(previewFile)} />
      )
    }

    // No inline preview for this format — the file is still attachable
    return <FileThumbnail name={previewFile.name} />
  }

  if (isRebrand) {
    return (
      <DarkModal
        bodyStyleOverrides={darkModalBodyStyles}
        colorScheme={colorScheme}
        isOpen={isOpen}
        isSaveHidden
        maxW='900px'
        onClose={onClose}
        title={previewFile.name}
        zIndex={1500}
      >
        {renderContent()}
      </DarkModal>
    )
  }

  return (
    <Modal
      closeOnOverlayClick
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      size='3xl'
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize='16px'>{previewFile.name}</ModalHeader>
        <ModalCloseButton />
        <ModalBody sx={modalBodyStyles}>{renderContent()}</ModalBody>
        <ModalFooter />
      </ModalContent>
    </Modal>
  )
}
